import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { CARD_TYPES } from 'src/features/cards/types';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useGetPricingPlanDetails } from 'src/features/pricingPlans/api/useGetPricingPlanDetails';
import { useListPricingPlans } from 'src/features/pricingPlans/api/useListPricingPlans';
import { useListSpecificPurposeAssignments } from 'src/features/pricingPlans/api/useListSpecificPurposeAssignments';
import { AssignmentFormModal } from 'src/features/pricingPlans/components/AssigmentFormModal';
import { CellCustomIdActions } from 'src/features/pricingPlans/components/CellCustomIdActions';
import ParkingPricingCards from 'src/features/pricingPlans/components/ParkingPricingCards';
import { PricingPlanFormModal } from 'src/features/pricingPlans/components/PricingPlanFormModal';
import { ASSIGMENT_CODE, type PricingPlan } from 'src/features/pricingPlans/types';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { SectionBlockBody } from 'src/registry/components/blocks/SectionBlock';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { useTitleStore } from 'src/stores/useTitleStore';
function ParkingPricingPlans() {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'parking-pricing-plans' });

    const [plansModalData, setPlansModalData] = React.useState<{ planId: null | number; isDelete: boolean }>({
        planId: null,
        isDelete: false,
    });
    const [assignmentModalData, setAssignmentModalData] = React.useState<{
        code: null | ASSIGMENT_CODE;
        planId: number | null;
    }>({
        code: null,
        planId: null,
    });
    const permissionManage = useHasPermissions([PERMISSION.PRICING_MANAGE]);

    const {
        data: pricingPlans,
        isLoading,
        isError,
        refetch: refetchPricingPlans,
    } = useListPricingPlans({
        cardType: CARD_TYPES.NORMAL,
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
        ...queryPaginationOptions,
    });
    const {
        data: specificPurposeAssignments,
        isLoading: areAssignmentsLoading,
        refetch: refetchAssignments,
    } = useListSpecificPurposeAssignments({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const rowSelection = useRowSelection({ sidePanelName: 'parking-pricing-plans' });
    const navigate = useNavigate();

    const { data: pricingPlanDetails, refetch: refetchPricingPlanDetails } = useGetPricingPlanDetails({
        planId: plansModalData.planId,
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const columnHelper = createColumnHelper<PricingPlan>();

    const columns = [
        columnHelper.accessor('plan_name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pricingPlansMessages.name),
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            cell: (props) => (
                <CellCustomIdActions
                    {...props}
                    editCallback={(planId) => {
                        setPlansModalData({ planId, isDelete: false });
                    }}
                    deleteCallback={(planId) => {
                        setPlansModalData({ planId, isDelete: true });
                    }}
                    showActions={permissionManage}
                />
            ),
            meta: {
                title: formatMessage(pricingPlansMessages.id),
            },
        }),
    ];

    const cardsData = React.useMemo(() => {
        const results: {
            code: ASSIGMENT_CODE;
            id: null | number;
        }[] = [
            { code: ASSIGMENT_CODE.NO_FREE_TIME, id: null },
            { code: ASSIGMENT_CODE.LATE_EXIT, id: null },
            { code: ASSIGMENT_CODE.LOST_TICKET, id: null },
            { code: ASSIGMENT_CODE.VIP_OVER_STAY, id: null },
        ];
        if (!specificPurposeAssignments) return results;
        specificPurposeAssignments.results.map((plan) => {
            results.map((result, index) => {
                if (plan.code !== result.code) return;
                results[index].id = plan.planId;
                return;
            });
        });
        return results;
    }, [specificPurposeAssignments?.results]);

    const resetPlansData = () => setPlansModalData({ planId: null, isDelete: false });
    const resetAssignmentsData = () =>
        setAssignmentModalData({
            code: null,
            planId: null,
        });

    return (
        <>
            {pricingPlanDetails && (
                <PricingPlanFormModal
                    selectedPricingPlan={pricingPlanDetails}
                    isDialogOpen={!!plansModalData.planId}
                    isDelete={plansModalData.isDelete}
                    removeTrigger
                    onCloseDialog={resetPlansData}
                    onSubmit={() => {
                        resetPlansData();
                        void refetchPricingPlans();
                        void refetchPricingPlanDetails();
                    }}
                />
            )}
            {assignmentModalData.code && (
                <AssignmentFormModal
                    isDialogOpen={!!assignmentModalData.code}
                    onCloseDialog={resetAssignmentsData}
                    selectedAssignment={assignmentModalData as { code: ASSIGMENT_CODE; planId: number }}
                    pricingPlans={pricingPlans}
                    onSubmit={() => {
                        resetAssignmentsData();
                        void refetchAssignments();
                    }}
                />
            )}
            <SectionBlockBody className="grid auto-rows-fr sm:grid-cols-2 lg:grid-cols-4">
                {cardsData.map((card) => (
                    <ParkingPricingCards
                        onChangeAssignment={setAssignmentModalData}
                        card={card}
                        key={card.code}
                        areAssignmentsLoading={areAssignmentsLoading}
                        showEdit={permissionManage}
                    />
                ))}
            </SectionBlockBody>
            <DataTable
                columns={columns}
                data={pricingPlans}
                pagination={pagination}
                sorting={sorting}
                search={search}
                refetch={refetchPricingPlans}
                semantics={{
                    title: formatMessage(pricingPlansMessages.pricingPlans),
                    entryName: formatMessage(pricingPlansMessages.pricingPlan),
                    entryNames: formatMessage(pricingPlansMessages.pricingPlans),
                }}
                isError={isError}
                isLoading={isLoading}
                components={{
                    newEntry: permissionManage ? PricingPlanFormModal : undefined,
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                rowSelection={rowSelection}
                dataTableBodyProps={{
                    onRowClickCallback: (row) => {
                        void navigate(`${row.id}`);
                    },
                }}
                enableLocalFilters={true}
            />
        </>
    );
}

export default ParkingPricingPlans;
